import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';

/** One arrow press. Small enough to aim with, large enough not to need holding. */
const KEYBOARD_STEP = 16;

interface DragState {
  pointerId: number;
  startX: number;
  startWidth: number;
  /** Latest previewed width — what gets committed on release. */
  width: number;
}

export interface TeamsPanelResizeHandleProps {
  /** Committed width, from the store. */
  width: number;
  minWidth: number;
  maxWidth: number;
  /** Width to return to on double-click. */
  defaultWidth: number;
  /**
   * Called on every pointer move. Deliberately NOT a state setter: the panel
   * writes the px straight onto the drawer's DOM nodes, so a drag repaints two
   * elements instead of re-rendering the chat list, thread and composer.
   */
  onPreviewWidth: (width: number) => void;
  /** Called once, on release — this is the one that reaches React state. */
  onCommitWidth: (width: number) => void;
}

/**
 * Drag handle on the drawer's left edge.
 *
 * Pointer events rather than mouse events, with pointer capture, so the drag
 * survives the cursor outrunning the 6px strip (which it always does) and works
 * from a trackpad or stylus. Capture also means the move/up events are retargeted
 * to this element, so React's own props are the listeners — there is nothing
 * registered on `window` to leak, which matters because the panel is mounted
 * outside the router outlet and lives for the whole session.
 */
export function TeamsPanelResizeHandle({
  width,
  minWidth,
  maxWidth,
  defaultWidth,
  onPreviewWidth,
  onCommitWidth,
}: TeamsPanelResizeHandleProps) {
  const { t } = useTranslation();
  const dragRef = useRef<DragState | null>(null);

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, Math.round(value))),
    [minWidth, maxWidth],
  );

  // Unmounting mid-drag (a route change cannot do it, but a logout can) would
  // otherwise leave the whole app unselectable with no handle left to release it.
  useEffect(
    () => () => {
      if (dragRef.current) document.body.style.userSelect = '';
    },
    [],
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      // Without this the browser starts a text selection from the mousedown and
      // the drag paints half the app blue.
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: width,
        width,
      };
      document.body.style.userSelect = 'none';
    },
    [width],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      // The panel is anchored right, so leftward travel is negative x and wider.
      const next = clamp(drag.startWidth + (drag.startX - event.clientX));
      drag.width = next;
      onPreviewWidth(next);
    },
    [clamp, onPreviewWidth],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      document.body.style.userSelect = '';
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      onCommitWidth(drag.width);
    },
    [onCommitWidth],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Same direction convention as the drag: left is wider.
      let next: number | undefined;
      if (event.key === 'ArrowLeft') next = width + KEYBOARD_STEP;
      else if (event.key === 'ArrowRight') next = width - KEYBOARD_STEP;
      else if (event.key === 'Home') next = minWidth;
      else if (event.key === 'End') next = maxWidth;
      if (next === undefined) return;
      event.preventDefault();
      onCommitWidth(clamp(next));
    },
    [width, minWidth, maxWidth, clamp, onCommitWidth],
  );

  const handleDoubleClick = useCallback(() => {
    onCommitWidth(clamp(defaultWidth));
  }, [clamp, defaultWidth, onCommitWidth]);

  return (
    <Box
      role="separator"
      aria-orientation="vertical"
      aria-label={t('teams.panel.resizeAriaLabel')}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuetext={t('teams.panel.resizeValueText', { width })}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      onDoubleClick={handleDoubleClick}
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        // Wider than the line it draws: 2px is impossible to grab, 6px is not.
        width: 6,
        display: 'flex',
        justifyContent: 'center',
        cursor: 'col-resize',
        zIndex: 1,
        // Stops the browser treating a touch drag here as a page scroll.
        touchAction: 'none',
        // The visible line is a pseudo-element so the hit area can stay larger
        // than the affordance, and it is invisible at rest — a permanent grey
        // bar down the edge reads as a scrollbar, which is exactly the thing it
        // must not be mistaken for.
        '&::after': {
          content: '""',
          width: '2px',
          height: '100%',
          backgroundColor: 'transparent',
        },
        // primary rather than a grey: the grey scale is inverted in dark mode,
        // so anything picked off it needs checking twice, and the accent reads
        // as "interactive" in both themes without that.
        '&:hover::after, &:focus-visible::after, &:active::after': {
          backgroundColor: 'primary.main',
        },
        // The line above is the focus indicator; a ring on a 6px strip is noise.
        '&:focus-visible': { outline: 'none' },
      }}
    />
  );
}
